import React, { useState, useMemo } from 'react'
import {
  Box,
  Chip,
  Collapse,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { HistoryEntry } from '../types'
import { formatDateFull, getParentOrigin } from '../helpers'
import CollapsibleSection from './CollapsibleSection'

function camelToLabel(key: string): string {
  return key
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (s) => s.toUpperCase())
    .trim()
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'number') {
    if (value > 1e12 && value < 2e13) return formatDateFull(value)
    return String(value)
  }
  if (Array.isArray(value)) return value.map(String).join(', ') || '—'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function DetailsTable({ details }: { details: Record<string, unknown> }) {
  const entries = Object.entries(details).filter(
    ([, v]) => v !== undefined && v !== null && v !== ''
  )
  if (entries.length === 0) return null
  return (
    <Table size='small' sx={{ maxWidth: 500 }}>
      <TableBody>
        {entries.map(([key, val]) => (
          <TableRow key={key} sx={{ '&:last-child td': { borderBottom: 0 } }}>
            <TableCell
              sx={{
                fontWeight: 600,
                color: 'text.secondary',
                width: 160,
                py: 0.5,
                fontSize: '0.8rem',
                verticalAlign: 'top',
              }}
            >
              {camelToLabel(key)}
            </TableCell>
            <TableCell sx={{ py: 0.5, fontSize: '0.8rem' }}>
              {typeof val === 'object' &&
              !Array.isArray(val) &&
              val !== null ? (
                <DetailsTable details={val as Record<string, unknown>} />
              ) : (
                formatValue(val)
              )}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

function CodeLinks({
  codes,
  urlBase,
  param,
}: {
  codes: string
  urlBase: string
  param: string
}) {
  if (!codes) return null
  const parentOrigin = getParentOrigin()
  return (
    <>
      {codes.split(', ').map((c, i) => (
        <React.Fragment key={c}>
          {i > 0 && ', '}
          <Link
            href={`${parentOrigin}/${urlBase}?${param}=${c}`}
            target='_blank'
            underline='hover'
            sx={{ fontSize: '0.8rem' }}
          >
            {c}
          </Link>
        </React.Fragment>
      ))}
    </>
  )
}

function flattenCodes(
  code: string | undefined,
  codes: string | string[] | undefined
): string {
  const all: string[] = []
  if (codes) {
    if (Array.isArray(codes)) all.push(...codes)
    else all.push(codes)
  } else if (code) {
    all.push(code)
  }
  return all.join(', ')
}

interface HistoryRowProps {
  entry: HistoryEntry
  index: number
}

function HistoryRow({ entry, index }: HistoryRowProps) {
  const [open, setOpen] = useState(false)

  const tCodes = flattenCodes(entry.tCode, entry.tCodes)
  const cCodes = flattenCodes(entry.cCode, entry.cCodes)
  const sCodes = flattenCodes(entry.sCode, entry.sCodes)
  const hasDetails = !!entry.details && Object.keys(entry.details).length > 0
  const hasExtra = tCodes || cCodes || sCodes || entry.aCode || hasDetails

  return (
    <Box
      sx={{
        borderBottom: '1px solid',
        borderColor: 'grey.200',
        bgcolor: index % 2 === 0 ? 'grey.50' : 'common.white',
      }}
    >
      <Box
        onClick={() => hasExtra && setOpen((o) => !o)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          py: 1,
          cursor: hasExtra ? 'pointer' : 'default',
          '&:hover': hasExtra ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        {hasExtra && (
          <ExpandMoreIcon
            sx={{
              fontSize: 18,
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              color: 'text.secondary',
            }}
          />
        )}
        {!hasExtra && <Box sx={{ width: 18 }} />}
        <Typography
          variant='body2'
          sx={{ width: 140, flexShrink: 0, color: 'text.secondary' }}
        >
          {formatDateFull(entry.timestamp)}
        </Typography>
        <Typography variant='body2' sx={{ fontWeight: 500, flex: 1 }}>
          {entry.name}
        </Typography>
        <Stack direction='row' spacing={0.5} sx={{ flexShrink: 0 }}>
          {tCodes && <Chip label='Teachers' size='small' variant='outlined' />}
          {cCodes && <Chip label='Classes' size='small' variant='outlined' />}
          {sCodes && <Chip label='Students' size='small' variant='outlined' />}
          {hasDetails && (
            <Chip
              label='Details'
              size='small'
              color='info'
              variant='outlined'
            />
          )}
        </Stack>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 2, pb: 1.5, pl: 5.5 }}>
          <Stack spacing={1}>
            {tCodes && (
              <Stack direction='row' spacing={1} alignItems='baseline'>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: 'text.secondary', width: 70 }}
                >
                  Teachers
                </Typography>
                <Box sx={{ fontSize: '0.8rem' }}>
                  <CodeLinks codes={tCodes} urlBase='teacher' param='t' />
                </Box>
              </Stack>
            )}
            {cCodes && (
              <Stack direction='row' spacing={1} alignItems='baseline'>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: 'text.secondary', width: 70 }}
                >
                  Classes
                </Typography>
                <Box sx={{ fontSize: '0.8rem' }}>
                  <CodeLinks codes={cCodes} urlBase='classroom' param='c' />
                </Box>
              </Stack>
            )}
            {sCodes && (
              <Stack direction='row' spacing={1} alignItems='baseline'>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: 'text.secondary', width: 70 }}
                >
                  Students
                </Typography>
                <Box sx={{ fontSize: '0.8rem' }}>
                  <CodeLinks codes={sCodes} urlBase='student' param='s' />
                </Box>
              </Stack>
            )}
            {entry.aCode && (
              <Stack direction='row' spacing={1} alignItems='baseline'>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: 'text.secondary', width: 70 }}
                >
                  Admin
                </Typography>
                <Typography variant='body2'>{entry.aCode}</Typography>
              </Stack>
            )}
            {hasDetails && (
              <Box sx={{ mt: 0.5 }}>
                <Typography
                  variant='caption'
                  sx={{ fontWeight: 600, color: 'text.secondary', mb: 0.5 }}
                >
                  Details
                </Typography>
                <DetailsTable
                  details={entry.details as Record<string, unknown>}
                />
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Box>
  )
}

interface HistorySectionProps {
  history: HistoryEntry[]
}

function HistorySection({ history }: HistorySectionProps) {
  const sorted = useMemo(
    () => [...history].sort((a, b) => b.timestamp - a.timestamp),
    [history]
  )

  return (
    <CollapsibleSection
      title='History'
      count={history.length}
      icon={HistoryIcon}
    >
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.200',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {sorted.map((entry, i) => (
          <HistoryRow key={entry.timestamp} entry={entry} index={i} />
        ))}
        {sorted.length === 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
            No history entries.
          </Typography>
        )}
      </Box>
    </CollapsibleSection>
  )
}

export default React.memo(HistorySection)
